import React, { useState, useCallback, useEffect } from "react";
import * as Sentry from "@sentry/react";
import {
  useVoiceAssistant,
  useDataChannel,
  useLocalParticipant,
  useTrackToggle,
  useRoomContext,
} from "@livekit/components-react";
import { useGlobalState } from "@/hooks/useGlobalState";
import { useMessages } from "@/hooks";
import APP_EVENTS from "@/configs/events";
import { get } from "@/utils/helpers";
import { setValue } from "@/utils/storage";
import log, { captureError } from "@/utils/logger";
import { ChatHeader } from "./components/ChatHeader";
import { ChatFooterText } from "./components/ChatFooterText";
import { ChatFooterVoice } from "./components/ChatFooterVoice";
import { useOnTranscriptions } from "./hooks/use-on-transcriptions";
import { useAgentTimeout } from "./providers/AgentTimeoutProvider";
import { ReconnectPrompt } from "./components/ReconnectPrompt";
import { VoiceMode } from "./components/VoiceMode";
import { CartProduct, Product } from "./types/product";
import { productToCartProduct } from "./utils/product-to-cart-product";
import { ConnectionState, Track } from "livekit-client";
import TextMode from "./components/TextMode";

interface Step2Props {
  setStep: (step: number) => void;
}

const Step2: React.FC<Step2Props> = ({ setStep }) => {
  const {
    chatMode,
    isMicrophoneEnabled,
    setIsMicrophoneEnabled,
    showReconnectPrompt,
    micPermission,
    setShowReconnectPrompt,
  } = useGlobalState();
  const room = useRoomContext();
  const {
    enabled: trackEnabled,
    toggle,
    track: localTrack,
  } = useTrackToggle({
    source: Track.Source.Microphone,
  });
  const { localParticipant } = useLocalParticipant();
  const { handleUserAction, handleAgentAction } = useAgentTimeout();
  const [cartItems, setCartItems] = useState<CartProduct[]>([]);
  const { messages, sendChat } = useMessages();
  const [productList, setProductList] = useState<Product[]>([]);
  const [productDescription, setProductDescription] = useState<any>(null);
  const agent = useVoiceAssistant();

  useEffect(() => {
    async function handleChatMode() {
      if (chatMode === "voice") {
        window.localStorage.setItem("chatMode", "voice");

        if (micPermission === "granted") {
          setIsMicrophoneEnabled(true);
        } else {
          setIsMicrophoneEnabled(false);
        }

        // Unmute agent voice
        const audio = document.querySelectorAll("#nut-widget-container audio");
        if (audio && audio.length > 0) {
          (audio[0] as HTMLAudioElement).muted = false;
        }
      } else if (chatMode === "text") {
        window.localStorage.setItem("chatMode", "text");
        setIsMicrophoneEnabled(false);

        // Mute agent voice
        const audio = document.querySelectorAll("#nut-widget-container audio");
        if (audio && audio.length > 0) {
          (audio[0] as HTMLAudioElement).muted = true;
        }

        if (localTrack?.track) {
          const mediaStreamsTracks = localTrack.track.mediaStream?.getTracks();
          mediaStreamsTracks?.forEach((track) => {
            track.stop();
            localParticipant.unpublishTrack(track);
          });
        }
      }
    }

    handleChatMode();
  }, [chatMode, micPermission, localTrack, localParticipant]);
  //
  // Sync global state and track state
  useEffect(() => {
    if (room?.state === ConnectionState.Connected) {
      if (isMicrophoneEnabled && !trackEnabled) {
        toggle();
      } else if (!isMicrophoneEnabled && trackEnabled) {
        toggle();
      }
    }
  }, [isMicrophoneEnabled, trackEnabled, toggle, room?.state]);

  useOnTranscriptions((_, participant) => {
    if (participant?.identity.startsWith("agent")) {
      handleAgentAction();
      setShowReconnectPrompt(false);
    } else if (participant?.sid === localParticipant.sid) {
      setValue("last_interaction", Date.now().toString());
      handleUserAction();
    }
  });

  const handleBasketClick = useCallback(() => {
    const currentUrl = window.location.origin;
    window.location.href = `${currentUrl}/cart`;
  }, []);

  // loading data from room/channel
  const handleDataMessage = useCallback(
    (decoded: any) => {
      log("Received data from LK:", decoded);
      if (
        get(decoded, "event_type") === APP_EVENTS.ADD_TO_CART &&
        get(decoded, "data", "")
      ) {
        const { data } = decoded;
        const { variant_id, product } = data;

        if (product && product.variants) {
          const reorderedVariants = [...product.variants].sort(
            (a: any, b: any) => {
              if (get(a, "id", "") === variant_id) return -1;
              if (get(b, "id", "") === variant_id) return 1;
              return 0;
            },
          );

          const updatedProduct = { ...product, variants: reorderedVariants };
          setProductList([updatedProduct]);
        }
      } else if (
        get(decoded, "event_type") === APP_EVENTS.PRODUCT_SHOW_DESCRIPTION
      ) {
        Sentry.captureMessage(APP_EVENTS.PRODUCT_SHOW_DESCRIPTION);
        log(APP_EVENTS.PRODUCT_SHOW_DESCRIPTION, decoded?.data);
        setProductDescription(decoded?.data.product);
      } else {
        const decodedProducts = (decoded?.products || []).map(
          (item: Product) => ({
            ...item,
            isProduct: true,
            timestamp: Date.now(),
          }),
        );

        setProductDescription(null);
        setProductList(decodedProducts);

        //create random id of 4 numbers
        const randomId = Math.floor(1000 + Math.random() * 9000);

        setValue(`products_${randomId}`, JSON.stringify(decodedProducts));
        sendChat(`products_${randomId}`);
      }

      const messageType =
        get(decoded, "event_type", "") || "VOICE_COMMAND_RECEIVED";
      const eventToParent = {
        type: messageType,
        data: {
          event_type: messageType,
          products: decoded?.data?.products,
          variant_id: decoded?.data?.variant_id,
          quantity: decoded?.data?.quantity,
          error: decoded?.error,
          url: decoded?.data?.url,
        },
      };

      window.postMessage(eventToParent, "*");
    },
    [sendChat],
  );

  const onDataReceived = useCallback(
    (msg: { topic: string; payload: ArrayBuffer }) => {
      const decoded = JSON.parse(new TextDecoder("utf-8").decode(msg.payload));

      if (msg.topic === "transcription") {
        console.log("transcription", decoded);
      } else {
        handleDataMessage(decoded);
      }
    },
    [handleDataMessage],
  );

  useDataChannel(onDataReceived);

  const handleAddToCart = useCallback(
    (product: Product | null) => {
      if (!product || !product.variants || product.variants.length === 0) {
        captureError("No product or variants available to add to cart.", {
          product,
        });
        return;
      }

      const type = "add_to_cart";
      const newCartItem: CartProduct = productToCartProduct(product);

      setCartItems((prevItems) => {
        const existingItem = prevItems.find(
          (item) => item.variant_id === newCartItem.variant_id,
        );

        if (existingItem) {
          return prevItems.map((item) =>
            item.variant_id === newCartItem.variant_id
              ? { ...item, quantity: item.quantity + 1 }
              : item,
          );
        }
        return [...prevItems, newCartItem];
      });

      // Fire event to parent window
      const eventToParent = {
        type,
        data: {
          event_type: type,
          variant_id: product?.variants?.[0]?.id,
          quantity: 1,
        },
      };
      window.postMessage(eventToParent, "*");
    },
    [setCartItems],
  );

  const handleClose = useCallback(() => {
    const audio = document.querySelectorAll("#nut-widget-container audio");
    if (audio && audio.length > 0) {
      (audio[0] as HTMLAudioElement).muted = true;
    }

    setTimeout(() => {
      setStep(0);
    }, 300);
  }, [setStep]);

  return (
    <div
      className={`absolute right-[46px] w-[400px] bottom-[46px] 
        [@media(max-width:440px)]:w-[calc(100vw-20px)] h-[min(calc(100vh-92px),794px)] 
        bg-white rounded-[24px] border border-gray-200 p-[20px] 
        flex flex-col overflow-hidden [@media(max-width:440px)]:right-[10px] 
        [@media(max-width:440px)]:bottom-[10px] [@media(max-width:440px)]:h-[calc(100dvh-20px)] ${
          agent.state === "speaking" && chatMode === "voice"
            ? "voice-chat-default"
            : ""
        }`}
    >
      <ChatHeader
        onClose={handleClose}
        agentState={agent.state}
        isMicrophoneEnabled={isMicrophoneEnabled}
      />
      {chatMode === "text" && (
        <>
          <TextMode
            onAddToCart={handleAddToCart}
            setProductDescription={setProductDescription}
            productDescription={productDescription}
            cartItems={cartItems}
            messages={messages}
          />
          {showReconnectPrompt && <ReconnectPrompt />}
          <ChatFooterText
            cartItems={cartItems.length}
            agentState={agent.state}
            sendChat={sendChat}
            onBasketClick={handleBasketClick}
          />
        </>
      )}
      {chatMode === "voice" && (
        <>
          <VoiceMode
            messages={messages}
            agentState={agent.state}
            setCartItems={setCartItems}
            cartItems={cartItems}
            productDescription={productDescription}
            setProductDescription={setProductDescription}
            productList={productList}
            onAddToCart={handleAddToCart}
          />
          {showReconnectPrompt && <ReconnectPrompt />}
          <ChatFooterVoice
            onShoppingClick={handleBasketClick}
            cartItems={cartItems.length}
          />
        </>
      )}
    </div>
  );
};

export default Step2;
